import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScanLine, CreditCard, Loader2, Check, X, ArrowLeft, Clock } from 'lucide-react';
import { http } from '../lib/api.js';
import { MethodIcon } from '../checkout/MethodIcon.jsx';
import { formatMoney } from '../lib/format.js';
import { playOutcome } from '../lib/sound.js';

const EASE = [0.2, 0.7, 0.2, 1];

/** How often the till asks whether the customer has finished paying. */
const POLL_MS = 2000;
/** After this long with no result, stop asking and let staff decide. */
const POLL_CEILING_MS = 5 * 60 * 1000;

/**
 * What the till can offer, built from what the merchant switched on.
 *
 * This list used to be three hardcoded rows, so a shop that took UPI at the
 * counter could not ring one up and a shop with no cash drawer was offered one
 * anyway. The counter half now comes from the server; the two gateway rows are
 * ways of handing the payment over rather than methods in their own right, so
 * they stay here — each shown only when it would actually work.
 */
function methodsFor(context) {
  const counter = (context?.counterMethods ?? []).map((m) => ({
    key: m.key,
    label: m.label,
    icon: m.icon,
    settlesAtCounter: m.settlesAtCounter,
    blurb: m.settlesAtCounter
      ? 'Take the money, done. No fee.'
      : 'Recorded now, confirmed when it arrives.',
  }));

  const gateway = [
    ...(context?.gateway?.qr
      ? [{ key: 'qr', label: 'Scan to pay', lucide: ScanLine, blurb: 'They pay on their own phone.' }]
      : []),
    ...(context?.gateway?.card
      ? [{ key: 'card', label: 'Card', lucide: CreditCard, blurb: 'Enter the card on this screen.' }]
      : []),
  ];

  return [...counter, ...gateway];
}

/**
 * The QR the customer scans.
 *
 * It encodes the checkout link, so what opens on their phone is the same hosted
 * checkout as everywhere else — the merchant's brand, the card form, the offline
 * methods. The till never sees a card number, which is the whole reason to
 * prefer this over keying one in.
 */
function CheckoutQr({ url }) {
  const [dataUrl, setDataUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    import('qrcode')
      .then((QR) => QR.toDataURL(url, { margin: 1, width: 560, errorCorrectionLevel: 'M' }))
      .then((d) => { if (!cancelled) setDataUrl(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [url]);

  return (
    <div className="flex flex-col items-center">
      <div className="rounded-2xl border bg-white p-4" style={{ borderColor: 'rgb(var(--line))' }}>
        {dataUrl ? (
          <img src={dataUrl} alt="Scan to pay" width={240} height={240} className="h-60 w-60" />
        ) : (
          <div className="grid h-60 w-60 place-items-center">
            <Loader2 className="h-6 w-6 animate-spin text-faint" aria-hidden="true" />
          </div>
        )}
      </div>
      <p className="mt-4 text-sm text-muted">Ask the customer to scan this with their phone camera.</p>
    </div>
  );
}

/**
 * Waits for the customer to finish.
 *
 * Polls this platform's own record rather than trusting anything the customer's
 * device says — the same rule the demo storefront's thank-you page follows, for
 * the same reason. A till that marks a sale paid because a phone said so is a
 * till that can be talked into giving away stock.
 */
/** How many times this payment has been declined so far, from the timeline the API renders verbatim. */
const declineCount = (payment) => (payment?.timeline ?? []).filter((t) => t.type === 'payment.failed').length;

function useSettlement({ paymentId, active, onSettled }) {
  const [elapsed, setElapsed] = useState(0);
  const [declined, setDeclined] = useState(null);
  const timer = useRef(null);
  const declinesSeen = useRef(0);

  useEffect(() => {
    if (!active || !paymentId) return undefined;

    let cancelled = false;
    let waited = 0;

    const poll = async () => {
      try {
        const payment = await http.get(`/dashboard/payments/${paymentId}`);
        if (cancelled) return;

        /**
         * Only two things end the wait: the money arrived, or the customer
         * walked away. A decline is neither (PL-3) — the checkout stays open
         * and they may try another card — so it is *said*, once per decline
         * rather than every two seconds, and the wait goes on. This used to
         * treat `failed` as settled and draw the green tick for it.
         */
        if (payment.status === 'succeeded' || payment.status === 'cancelled') {
          // The cashier is usually bagging goods, not watching the screen.
          playOutcome(payment.status === 'succeeded' ? 'approved' : 'declined');
          onSettled(payment);
          return;
        }
        const declines = declineCount(payment);
        if (payment.status === 'failed' && declines > declinesSeen.current) {
          declinesSeen.current = declines;
          playOutcome('declined');
          setDeclined({ attempts: declines, message: payment.failure?.message ?? null });
        }
      } catch {
        // A flaky shop wifi is not a failed payment. Keep asking.
      }

      if (cancelled) return;
      waited += POLL_MS;
      setElapsed(waited);
      if (waited < POLL_CEILING_MS) timer.current = setTimeout(poll, POLL_MS);
    };

    timer.current = setTimeout(poll, POLL_MS);
    return () => { cancelled = true; clearTimeout(timer.current); };
  }, [paymentId, active, onSettled]);

  return { elapsed, declined, givenUp: elapsed >= POLL_CEILING_MS };
}

function Waiting({ paymentId, onSettled, children }) {
  const { givenUp, declined } = useSettlement({ paymentId, active: true, onSettled });

  return (
    <div className="flex flex-col items-center">
      {children}
      {declined && (
        <div role="status" className="mt-5 w-full rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3 text-center">
          <p className="text-sm font-medium text-amber-800">
            Declined{declined.attempts > 1 ? ` (${declined.attempts} attempts)` : ''} — nothing has been taken.
          </p>
          <p className="mt-0.5 text-xs text-amber-700">
            {declined.message ?? 'That card could not be charged.'} The customer can try another card.
          </p>
        </div>
      )}
      <div className="mt-6 flex items-center gap-2.5 text-sm text-muted">
        {givenUp ? (
          <span>No payment yet. Leave this open, or close it and check the payment later.</span>
        ) : (
          <>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Waiting for the customer…
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The sheet that takes over once an amount is rung up.
 *
 * Three ways to pay, then whatever that method needs. It never closes itself on
 * a result — the cashier dismisses it, because a receipt that vanishes on its
 * own is a receipt nobody read.
 */
export function PaySheet({ open, amount, currency, sale, context, onPick, onDone, onClose, busy, error }) {
  const [settled, setSettled] = useState(null);

  useEffect(() => { if (!open) setSettled(null); }, [open]);

  // Read by the Escape handler, which must not re-subscribe on every render.
  const outcomeRef = useRef(null);

  /**
   * Escape closes it, like every other dialog in the product.
   *
   * The till's own keyboard handler uses Escape to clear the amount, but it
   * stands down while this is open — so without this the key did nothing at
   * all, which on a counter is the one moment somebody is in a hurry.
   *
   * Once a result is showing, Escape finishes the sale rather than abandoning
   * it: the same thing the X in the corner does, for the same reason.
   */
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      if (outcomeRef.current) onDone?.();
      else onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, onDone]);

  const outcome = settled ?? (sale?.payment?.status === 'succeeded' ? sale.payment : null);
  // A settled outcome is not always a sale. Only `succeeded` earns the tick.
  const paid = outcome?.status === 'succeeded';
  const methods = methodsFor(context);
  outcomeRef.current = outcome;

  /**
   * A method the merchant collects but does not watch arrive — a bank transfer,
   * a cheque. The sale is recorded and the payment waits: nobody may mark it
   * paid until the money is actually there.
   */
  const awaiting = !outcome && sale?.awaitingConfirmation ? sale : null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
        >
          <div
            className="absolute inset-0 bg-black/45"
            onClick={outcome ? onDone : onClose}
            aria-hidden="true"
          />

          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Take payment"
            className="relative w-full max-w-lg overflow-hidden rounded-t-2xl border bg-surface sm:rounded-2xl"
            style={{ borderColor: 'rgb(var(--line))' }}
            initial={{ y: 40 }}
            animate={{ y: 0 }}
            exit={{ y: 30 }}
            transition={{ duration: 0.26, ease: EASE }}
          >
            <div
              className="flex items-center justify-between border-b px-5 py-4"
              style={{ borderColor: 'rgb(var(--line-soft))' }}
            >
              <div className="flex items-center gap-3">
                {sale && !outcome && (
                  <button
                    type="button"
                    onClick={onClose}
                    className="btn-icon"
                    aria-label="Back"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                )}
                <div>
                  <p className="eyebrow">{outcome ? (paid ? 'Paid' : 'Not paid') : 'Taking'}</p>
                  <p className="tabular text-xl font-semibold text-ink">{formatMoney(amount, currency)}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={outcome ? onDone : onClose}
                className="btn-icon"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="px-5 py-6">
              {error && (
                <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3.5 py-3">
                  <p className="text-sm font-medium text-red-700">{error.message}</p>
                </div>
              )}

              {/* ------------------------------ paid ------------------------------ */}
              {outcome && paid && (
                <div className="flex flex-col items-center py-4 text-center">
                  <motion.div
                    className="grid h-16 w-16 place-items-center rounded-2xl border border-emerald-200 bg-emerald-50 text-emerald-600"
                    initial={{ scale: 0.85 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.3, ease: EASE }}
                  >
                    <Check className="h-8 w-8" strokeWidth={2.4} aria-hidden="true" />
                  </motion.div>
                  <p className="mt-4 text-lg font-semibold text-ink">
                    {formatMoney(outcome.amount, outcome.currency)} taken
                  </p>
                  <p className="mt-1 font-mono text-xs text-muted">{outcome.id}</p>

                  <button type="button" onClick={onDone} className="btn-primary mt-7 h-12 w-full text-base">
                    Next sale
                  </button>
                </div>
              )}

              {/* ---------------------------- not paid ---------------------------- */}
              {outcome && !paid && (
                <div className="flex flex-col items-center py-4 text-center">
                  <div className="grid h-16 w-16 place-items-center rounded-2xl border border-red-200 bg-red-50 text-red-600">
                    <X className="h-8 w-8" strokeWidth={2.4} aria-hidden="true" />
                  </div>
                  <p className="mt-4 text-lg font-semibold text-ink">Nothing was taken</p>
                  <p className="mt-1 text-sm text-muted">
                    {outcome.status === 'cancelled'
                      ? 'The customer cancelled the payment.'
                      : 'This payment did not go through.'}
                  </p>
                  <p className="mt-1 font-mono text-xs text-muted">{outcome.id}</p>

                  <button type="button" onClick={onDone} className="btn-primary mt-7 h-12 w-full text-base">
                    Start again
                  </button>
                </div>
              )}

              {/* --------------------------- choose a method ---------------------- */}
              {!outcome && !awaiting && !sale && (
                <div className="space-y-2.5">
                  {methods.map((m) => (
                    <button
                      key={m.key}
                      type="button"
                      disabled={busy}
                      onClick={() => onPick(m.key)}
                      className="flex w-full items-center gap-3.5 rounded-xl border bg-surface px-4 py-3.5 text-left transition-colors hover:bg-surface-2 disabled:opacity-50"
                      style={{ borderColor: 'rgb(var(--line))' }}
                    >
                      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border bg-surface-2 text-ink-soft"
                        style={{ borderColor: 'rgb(var(--line))' }}>
                        {m.lucide
                          ? <m.lucide className="h-5 w-5" aria-hidden="true" />
                          : <MethodIcon icon={m.icon} className="h-5 w-5" />}
                      </span>
                      <span className="flex-1">
                        <span className="block text-[15px] font-medium text-ink">{m.label}</span>
                        <span className="block text-xs text-muted">{m.blurb}</span>
                      </span>
                      {busy && <Loader2 className="h-4 w-4 animate-spin text-faint" aria-hidden="true" />}
                    </button>
                  ))}

                  {methods.length === 0 && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5">
                      <p className="text-sm font-medium text-amber-800">Nothing is switched on for the till</p>
                      <p className="mt-1 text-xs leading-relaxed text-amber-700">
                        Someone with access to settings needs to enable a payment method
                        with “At the till” ticked.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* ------------------- taken, but not money yet -------------------- */}
              {awaiting && (
                <div className="flex flex-col items-center py-4 text-center">
                  <div className="grid h-16 w-16 place-items-center rounded-2xl border border-amber-200 bg-amber-50 text-amber-700">
                    <Clock className="h-8 w-8" strokeWidth={2} aria-hidden="true" />
                  </div>
                  <p className="mt-4 text-lg font-semibold text-ink">Recorded, not paid yet</p>
                  <p className="mt-1 max-w-sm text-sm leading-relaxed text-muted">
                    {/*
                      * The honest wording matters here. A cheque in the till is
                      * not money in the account, so this sale stays open until
                      * somebody confirms it landed — from the payment itself.
                      */}
                    {awaiting.instructions
                      || 'Mark it received from the payment once the money reaches your account.'}
                  </p>
                  <p className="mt-3 font-mono text-xs text-muted">{awaiting.payment.id}</p>

                  <button type="button" onClick={onDone} className="btn-primary mt-7 h-12 w-full text-base">
                    Next sale
                  </button>
                </div>
              )}

              {/* ------------------------------- QR ------------------------------- */}
              {!outcome && sale?.method === 'qr' && sale.checkoutUrl && (
                <Waiting paymentId={sale.payment.id} onSettled={setSettled}>
                  <CheckoutQr url={sale.checkoutUrl} />
                </Waiting>
              )}

              {/* ---------------------------- card on screen ---------------------- */}
              {!outcome && sale?.method === 'card' && sale.checkoutUrl && (
                <Waiting paymentId={sale.payment.id} onSettled={setSettled}>
                  {/*
                    * The same hosted checkout, in a frame. The card is entered on
                    * the checkout's own origin and tokenized there, so keying a
                    * card in at the counter puts the till no closer to card data
                    * than the QR does.
                    */}
                  <iframe
                    src={sale.checkoutUrl}
                    title="Card payment"
                    className="h-[420px] w-full rounded-xl border"
                    style={{ borderColor: 'rgb(var(--line))' }}
                    allow="payment *"
                  />
                </Waiting>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default PaySheet;
